import React, { useEffect, useState } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  Users, UserPlus, Trash2, MapPin,
  Calendar, Heart, Baby, CheckCircle,
  AlertCircle, Clock, Globe, Edit2
} from "lucide-react";

/* ================= DEFAULT ================= */
const createEmptySibling = () => ({
  relation: "",
  name: "",
  surname: "",
  maritalStatus: "",
  dateOfBirth: "",
  dateOfDeath: "",
  placeOfBirth: "",
  currentAddress: "",
  isInCanada: false,
  canadaAddress: "",
});

const SiblingForm = () => {
  const [siblings, setSiblings] = useState([createEmptySibling()]);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchSiblings();
  }, []);

  const fetchSiblings = async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("get", "/user/getSibling");
      const list = Array.isArray(res?.data?.sibling) ? res.data.sibling : [];
      const apiStatus = res?.data?.status || null;
      setStatus(apiStatus);

      if (list.length > 0) {
        // Formatting dates from ISO string to YYYY-MM-DD for native input
        const formatted = list.map((s) => ({
          ...createEmptySibling(),
          ...s,
          dateOfBirth: s.dateOfBirth ? s.dateOfBirth.split('T')[0] : "",
          dateOfDeath: s.dateOfDeath ? s.dateOfDeath.split('T')[0] : "",
        }));
        setSiblings(formatted);
        setPreviewMode(true);
      } else {
        setPreviewMode(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addSibling = () => setSiblings((prev) => [...prev, createEmptySibling()]);

  const removeSibling = (index) => {
    if (siblings.length === 1) return;
    setSiblings((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setSiblings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === "isInCanada" && !value) {
        updated[index].canadaAddress = "";
      }
      return updated;
    });
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   try {
  //     setLoading(true);
  //     await makeRequestAuth("post", "/user/createOrUpdateSibling", {
  //       sibling: siblings,
  //     });
  //     showSuccessToast("Family records updated ✅");
  //     fetchSiblings();
  //   } catch (err) {
  //     showErrorToast(err?.response?.data?.message || "Error saving details");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // remove _id before sending
      const cleanedSiblings = siblings.map(({ _id, ...rest }) => rest);

      await makeRequestAuth("post", "/user/createOrUpdateSibling", {
        sibling: cleanedSiblings,
      });

      showSuccessToast("Family records updated ✅");
      fetchSiblings();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Error saving details");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle size={14} />, label: "Approved" },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Rejected" }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 antialiased">
      <ToastContainer />

      {/* HEADER SECTION */}
      <div className="bg-[#132b45] rounded-t-[2rem] p-8 text-white flex flex-col md:flex-row justify-between items-center gap-6 shadow-2xl">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-500/20 rounded-2xl">
            <Users size={30} className="text-blue-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Sibling Records</h1>
            <p className="text-blue-100/60 text-xs mt-1">Provide details of all your brothers and sisters</p>
          </div>
        </div>
        {previewMode && (
          <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${STATUS_CONFIG[status]?.color || "bg-slate-100 text-slate-600"}`}>
            {STATUS_CONFIG[status]?.icon || <Clock size={14} />} {STATUS_CONFIG[status]?.label || "Processing"}
          </div>
        )}
      </div>

      <div className="bg-white rounded-b-[2rem] shadow-xl border-x border-b border-gray-100 p-8">
        {previewMode ? (
          /* ================= PREVIEW MODE ================= */
          <div className="space-y-6">
            {siblings.map((s, i) => (
              <div key={i} className="border border-gray-100 rounded-3xl p-6 bg-gray-50/50 hover:bg-white transition-all duration-300">
                <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-3">
                  <div className="w-6 h-6 rounded-full bg-[#132b45] text-white flex items-center justify-center text-[10px] font-bold">0{i + 1}</div>
                  <h3 className="font-bold text-[#132b45] uppercase text-xs tracking-widest">{s.relation || "Sibling"}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  <PreviewItem icon={<UserPlus size={16} />} label="Full Name" value={`${s.name} ${s.surname}`} />
                  <PreviewItem icon={<Heart size={16} />} label="Marital Status" value={s.maritalStatus} capitalize />
                  <PreviewItem icon={<Calendar size={16} />} label="Date of Birth" value={formatDate(s.dateOfBirth)} />
                  <PreviewItem icon={<Globe size={16} />} label="Birth Place" value={s.placeOfBirth} />
                  <PreviewItem icon={<MapPin size={16} />} label="In Canada" value={s.isInCanada ? "Yes" : "No"} highlight={s.isInCanada} />
                  <PreviewItem icon={<Baby size={16} />} label="Status" value={s.dateOfDeath ? "Deceased" : "Alive"} />

                  <div className="sm:col-span-2 lg:col-span-3">
                    <PreviewItem icon={<MapPin size={16} />} label="Address" value={s.currentAddress} />
                  </div>
                  {s.isInCanada && (
                    <div className="sm:col-span-2 lg:col-span-3 bg-blue-50 p-4 rounded-2xl border border-blue-100">
                      <PreviewItem icon={<Globe size={16} />} label="Canada Residential Address" value={s.canadaAddress} />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {(status === "reject" || !status) && (
              <div className="flex justify-center pt-8">
                <button
                  onClick={() => setPreviewMode(false)}
                  className="flex items-center gap-2 bg-[#132b45] text-white px-8 py-3.5 rounded-2xl font-bold hover:bg-black transition-all"
                >
                  <Edit2 size={18} /> Edit Sibling Details
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ================= FORM MODE ================= */
          <form onSubmit={handleSubmit} className="space-y-10">
            {siblings.map((item, index) => (
              <div key={index} className="relative border-2 border-gray-50 rounded-[2rem] p-8 bg-white">
                <div className="flex justify-between items-center mb-8">
                  <span className="bg-gray-100 text-gray-500 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Sibling {index + 1}</span>
                  {siblings.length > 1 && (
                    <button type="button" onClick={() => removeSibling(index)} className="text-rose-400 hover:text-rose-600 p-2 hover:bg-rose-50 rounded-full transition-all">
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SelectField label="Relationship" value={item.relation} onChange={(v) => handleChange(index, "relation", v)} options={["Brother", "Sister"]} required />
                  <SelectField label="Marital Status" value={item.maritalStatus} onChange={(v) => handleChange(index, "maritalStatus", v)} options={["Single", "Married", "Common-law", "Divorced", "Widowed", "Separated"]} required />

                  <InputField label="First Name" value={item.name} onChange={(v) => handleChange(index, "name", v)} required />
                  <InputField label="Surname" value={item.surname} onChange={(v) => handleChange(index, "surname", v)} required />

                  <DateField label="Date of Birth" value={item.dateOfBirth} onChange={(v) => handleChange(index, "dateOfBirth", v)} required />
                  <DateField label="Date of Death" value={item.dateOfDeath} onChange={(v) => handleChange(index, "dateOfDeath", v)} />

                  <InputField label="City/Place of Birth" value={item.placeOfBirth} onChange={(v) => handleChange(index, "placeOfBirth", v)} />

                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Current Residential Address</label>
                    <textarea
                      rows="2"
                      value={item.currentAddress}
                      onChange={(e) => handleChange(index, "currentAddress", e.target.value)}
                      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all"
                      placeholder="Full address"
                    />
                  </div>

                  <div className="md:col-span-2 p-6 bg-blue-50/50 rounded-2xl border border-blue-100">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-5 h-5 rounded border-gray-300 text-[#132b45] focus:ring-[#132b45]"
                        checked={item.isInCanada}
                        onChange={(e) => handleChange(index, "isInCanada", e.target.checked)}
                      />
                      <span className="font-bold text-gray-700">Presently living in Canada?</span>
                    </label>

                    {item.isInCanada && (
                      <div className="mt-4 animate-in fade-in zoom-in-95">
                        <InputField label="Canada Residential Address" value={item.canadaAddress} onChange={(v) => handleChange(index, "canadaAddress", v)} required placeholder="Provide full Canada address" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row gap-4 pt-4 pb-10">
              <button type="button" onClick={addSibling} className="flex-1 border-2 border-dashed border-gray-200 text-gray-500 font-bold py-4 rounded-2xl hover:border-[#132b45] hover:text-[#132b45] transition-all flex items-center justify-center gap-2">
                <UserPlus size={20} /> Add Another Sibling
              </button>
              <button type="submit" disabled={loading} className="flex-[1.5] bg-[#132b45] text-white font-bold py-4 rounded-2xl shadow-lg hover:bg-black transition-all flex items-center justify-center">
                {loading ? "Processing..." : "Save Family Records"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/* ================= HELPERS ================= */
const InputField = ({ label, value, onChange, required, placeholder }) => (
  <div>
    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">{label} {required && "*"}</label>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, required }) => (
  <div>
    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">{label} {required && "*"}</label>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all cursor-pointer"
    >
      <option value="">Select {label}</option>
      {options.map(opt => <option key={opt} value={opt.toLowerCase()}>{opt}</option>)}
    </select>
  </div>
);

const DateField = ({ label, value, onChange, required }) => (
  <div>
    <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">{label} {required && "*"}</label>
    <input
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      max={new Date().toISOString().split("T")[0]}
      className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl px-5 py-3.5 focus:border-[#132b45] focus:bg-white outline-none transition-all"
    />
  </div>
);

const PreviewItem = ({ icon, label, value, highlight, capitalize }) => (
  <div className="flex items-start gap-3">
    <div className="mt-1 text-gray-400">{icon}</div>
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest font-black text-gray-400 mb-0.5">{label}</span>
      <span className={`text-sm font-bold break-words leading-tight ${highlight ? 'text-blue-600' : 'text-[#132b45]'} ${capitalize ? 'capitalize' : ''}`}>
        {value || <span className="text-gray-300 font-normal italic">N/A</span>}
      </span>
    </div>
  </div>
);

export default SiblingForm;